import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormLabel } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";
import type { CustomerFormData } from "../types";

interface InlineCustomerFormProps {
  formData: CustomerFormData;
  onChange: (data: CustomerFormData) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export function InlineCustomerForm({
  formData,
  onChange,
  onSubmit,
  onCancel,
  isSubmitting,
}: InlineCustomerFormProps) {
  return (
    <div className="space-y-4 border rounded-lg p-4 bg-muted/50">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Luo uusi asiakas</h3>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-4">
        <div>
          <FormLabel>Asiakastyyppi *</FormLabel>
          <Select
            value={formData.type}
            onValueChange={(value: "private" | "company" | "municipality" | "estate") =>
              onChange({ ...formData, type: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="private">Yksityishenkilö</SelectItem>
              <SelectItem value="company">Yritys</SelectItem>
              <SelectItem value="municipality">Kunta</SelectItem>
              <SelectItem value="estate">Kuolinpesä</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {formData.type === "private" ? (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FormLabel>Etunimi *</FormLabel>
              <Input
                value={formData.first_name}
                onChange={(e) => onChange({ ...formData, first_name: e.target.value })}
              />
            </div>
            <div>
              <FormLabel>Sukunimi *</FormLabel>
              <Input
                value={formData.last_name}
                onChange={(e) => onChange({ ...formData, last_name: e.target.value })}
              />
            </div>
          </div>
        ) : formData.type === "estate" ? (
          <>
            <div>
              <FormLabel>Kuolinpesän nimi *</FormLabel>
              <Input
                value={formData.company_name}
                onChange={(e) => onChange({ ...formData, company_name: e.target.value })}
                placeholder="esim. Matti Meikäläisen kuolinpesä"
              />
            </div>
            <div>
              <FormLabel>Yhteyshenkilö (c/o) *</FormLabel>
              <Input
                value={formData.contact_person}
                onChange={(e) => onChange({ ...formData, contact_person: e.target.value })}
                placeholder="esim. Maija Meikäläinen"
              />
            </div>
          </>
        ) : (
          <div>
            <FormLabel>Yrityksen nimi *</FormLabel>
            <Input
              value={formData.company_name}
              onChange={(e) => onChange({ ...formData, company_name: e.target.value })}
            />
          </div>
        )}

        {formData.type !== "private" && formData.type !== "estate" && (
          <div>
            <FormLabel>Y-tunnus</FormLabel>
            <Input
              value={formData.business_id}
              onChange={(e) => onChange({ ...formData, business_id: e.target.value })}
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel>Sähköposti</FormLabel>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => onChange({ ...formData, email: e.target.value })}
            />
          </div>
          <div>
            <FormLabel>Puhelin *</FormLabel>
            <Input
              value={formData.phone}
              onChange={(e) => onChange({ ...formData, phone: e.target.value })}
            />
          </div>
        </div>

        <div>
          <FormLabel>Osoite *</FormLabel>
          <Input
            value={formData.address}
            onChange={(e) => onChange({ ...formData, address: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel>Postinumero *</FormLabel>
            <Input
              value={formData.postal_code}
              onChange={(e) => onChange({ ...formData, postal_code: e.target.value })}
            />
          </div>
          <div>
            <FormLabel>Kaupunki *</FormLabel>
            <Input
              value={formData.city}
              onChange={(e) => onChange({ ...formData, city: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Peruuta
        </Button>
        <Button type="button" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? "Luodaan..." : "Tallenna asiakas"}
        </Button>
      </div>
    </div>
  );
}
